<template>
  <Dialog :open="open" @update:open="handleClose" :title="dialogTitle" size="lg">
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- Model Information Section -->
      <div class="space-y-4">
        <div>
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Model Information</h4>
          <div class="space-y-4">
            <!-- Name Input -->
            <div>
              <label
                for="custom-model-name"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Name <span class="text-red-500">*</span>
              </label>
              <Input
                id="custom-model-name"
                v-model="formState.name"
                placeholder="e.g., My Custom Model"
                :disabled="isSubmitting"
                :error="errors.name"
                class="w-full"
              />
              <p v-if="errors.name" class="mt-1.5 text-xs text-red-600 dark:text-red-400">
                {{ errors.name }}
              </p>
              <p v-else class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                A friendly name for your custom model
              </p>
            </div>

            <!-- Slug Input -->
            <div>
              <label
                for="custom-model-slug"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Slug <span class="text-red-500">*</span>
              </label>
              <Input
                id="custom-model-slug"
                v-model="formState.slug"
                placeholder="e.g., my-custom-model"
                :disabled="isSubmitting || isEditMode"
                :error="errors.slug"
                class="w-full"
              />
              <p v-if="errors.slug" class="mt-1.5 text-xs text-red-600 dark:text-red-400">
                {{ errors.slug }}
              </p>
              <p v-else class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                {{
                  isEditMode
                    ? 'Slug cannot be changed after creation'
                    : 'Alphanumeric characters, dots, underscores, and hyphens only'
                }}
              </p>
            </div>

            <!-- SDK Type Select -->
            <div>
              <label
                for="custom-model-sdk-type"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                SDK Type <span class="text-red-500">*</span>
              </label>
              <select
                id="custom-model-sdk-type"
                v-model="formState.sdkType"
                class="w-full h-10 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:border-transparent text-sm text-gray-900 dark:text-white"
                :disabled="isSubmitting || isEditMode"
              >
                <option value="openai_compatible">OpenAI Compatible</option>
                <option value="anthropic">Anthropic</option>
                <option value="bedrock">AWS Bedrock</option>
                <option value="google">Google AI Studio</option>
                <option value="vertex_ai">Google Vertex AI</option>
                <option value="groq">Groq</option>
              </select>
              <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                {{ isEditMode ? 'SDK type cannot be changed after creation' : 'The underlying SDK/provider to use for this endpoint' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Connection Settings Section -->
        <div>
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Connection Settings
          </h4>
          <div class="space-y-4">
            <!-- Base URL Input (Only for openai_compatible) -->
            <div v-if="formState.sdkType === 'openai_compatible'">
              <label
                for="custom-model-base-url"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Base URL <span class="text-red-500">*</span>
              </label>
              <Input
                id="custom-model-base-url"
                v-model="formState.baseUrl"
                type="url"
                placeholder="https://api.example.com/v1"
                :disabled="isSubmitting"
                :error="errors.baseUrl"
                class="w-full"
              />
              <p v-if="errors.baseUrl" class="mt-1.5 text-xs text-red-600 dark:text-red-400">
                {{ errors.baseUrl }}
              </p>
              <p v-else class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                HTTPS URL to your model endpoint
              </p>
            </div>

            <!-- API Key Input -->
            <div>
              <label
                for="custom-model-api-key"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                API Key <span v-if="!isEditMode" class="text-red-500">*</span>
              </label>
              <Input
                id="custom-model-api-key"
                v-model="formState.apiKey"
                type="password"
                :placeholder="
                  isEditMode ? 'Leave empty to keep existing key' : 'Enter your API key'
                "
                :disabled="isSubmitting"
                :error="errors.apiKey"
                class="w-full"
              />
              <p v-if="errors.apiKey" class="mt-1.5 text-xs text-red-600 dark:text-red-400">
                {{ errors.apiKey }}
              </p>
              <p v-else class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                {{
                  isEditMode
                    ? 'API key is encrypted and never returned. Provide a new key to update.'
                    : 'Your API key will be encrypted and stored securely'
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- Cost Tracking Section (Optional) -->
        <div>
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Cost Tracking (Optional)
          </h4>
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Track costs for your custom model. Leave empty if not applicable.
          </p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Input Cost -->
            <div>
              <label
                for="custom-model-input-cost"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Input Cost (per 1M tokens)
              </label>
              <Input
                id="custom-model-input-cost"
                v-model.number="formState.inputCost"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                :disabled="isSubmitting"
                class="w-full"
              />
              <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Cost in USD per 1 million input tokens
              </p>
            </div>

            <!-- Output Cost -->
            <div>
              <label
                for="custom-model-output-cost"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Output Cost (per 1M tokens)
              </label>
              <Input
                id="custom-model-output-cost"
                v-model.number="formState.outputCost"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                :disabled="isSubmitting"
                class="w-full"
              />
              <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Cost in USD per 1 million output tokens
              </p>
            </div>
          </div>
        </div>

        <!-- Enabled Toggle -->
        <Card class="flex items-center justify-between">
          <div>
            <label
              for="custom-model-enabled"
              class="text-sm font-medium text-gray-900 dark:text-white"
            >
              Enable Model
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {{
                formState.isEnabled
                  ? 'Model is enabled and available for use'
                  : 'Model is disabled and not available for use'
              }}
            </p>
          </div>
          <Switch id="custom-model-enabled" v-model="formState.isEnabled" :disabled="isSubmitting" />
        </Card>
      </div>

      <!-- Security Notice -->
      <div
        class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-blue-800 dark:text-blue-300">Security Notice</h3>
            <div class="mt-1 text-sm text-blue-700 dark:text-blue-400">
              <p>
                Your API key is encrypted before storage and will never be returned in API
                responses. Only you can update or replace it.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Display -->
      <Alert v-if="generalError" variant="error">
        {{ generalError }}
      </Alert>

      <!-- Actions -->
      <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" @click="handleClose" :disabled="isSubmitting">
          Cancel
        </Button>
        <Button type="submit" variant="primary" :loading="isSubmitting">
          {{ isEditMode ? 'Update Model' : 'Create Model' }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Dialog, Button, Input, Alert, Switch, Card } from '@asabdev/asab-ui';
  import { validateName, validateSlug, validateUrl } from '../utils/validation';
  import type { CustomModelResponse, SdkType, CreateCustomModelRequest, UpdateCustomModelRequest } from '../types';

  interface Props {
    open: boolean;
    editModel?: CustomModelResponse | null;
  }

  interface Emits {
    (e: 'update:open', value: boolean): void;
    (e: 'created', model: CreateCustomModelRequest): void;
    (e: 'updated', model: UpdateCustomModelRequest): void;
    (e: 'cancel'): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    editModel: null,
  });

  const emit = defineEmits<Emits>();

  // Form state
  interface FormState {
    name: string;
    slug: string;
    sdkType: SdkType;
    baseUrl: string;
    apiKey: string;
    inputCost: number | undefined;
    outputCost: number | undefined;
    isEnabled: boolean;
  }

  const formState = ref<FormState>({
    name: '',
    slug: '',
    sdkType: 'openai_compatible',
    baseUrl: '',
    apiKey: '',
    inputCost: undefined,
    outputCost: undefined,
    isEnabled: true,
  });

  const isSubmitting = ref(false);
  const errors = ref<Record<string, string>>({});
  const generalError = ref<string | null>(null);

  // Computed properties
  const isEditMode = computed(() => !!props.editModel);

  const dialogTitle = computed(() => {
    return isEditMode.value ? 'Edit Custom Model' : 'Create Custom Model';
  });

  // Methods
  const validateForm = (): boolean => {
    errors.value = {};
    generalError.value = null;

    // Validate name
    const nameValidation = validateName(formState.value.name);
    if (!nameValidation.isValid) {
      errors.value.name = nameValidation.error || 'Invalid name';
    }

    // Validate slug (only for create mode)
    if (!isEditMode.value) {
      const slugValidation = validateSlug(formState.value.slug);
      if (!slugValidation.isValid) {
        errors.value.slug = slugValidation.error || 'Invalid slug';
      }
    }

    // Validate base URL (only for openai_compatible)
    if (formState.value.sdkType === 'openai_compatible') {
      const urlValidation = validateUrl(formState.value.baseUrl);
      if (!urlValidation.isValid) {
        errors.value.baseUrl = urlValidation.error || 'Invalid URL';
      }
    }

    // Validate API key (required for create, optional for edit)
    if (!isEditMode.value) {
      if (!formState.value.apiKey.trim()) {
        errors.value.apiKey = 'API key is required';
      }
    }

    // Validate cost inputs (if provided)
    if (formState.value.inputCost !== undefined && formState.value.inputCost < 0) {
      errors.value.inputCost = 'Input cost must be 0 or greater';
    }

    if (formState.value.outputCost !== undefined && formState.value.outputCost < 0) {
      errors.value.outputCost = 'Output cost must be 0 or greater';
    }

    return Object.keys(errors.value).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    isSubmitting.value = true;

    // Emit the appropriate event based on mode
    // Note: The parent component will handle the actual API call
    // This component just validates and emits the data
    if (isEditMode.value) {
      const updateData: UpdateCustomModelRequest = {
        name: formState.value.name.trim(),
        base_url: formState.value.sdkType === 'openai_compatible' ? formState.value.baseUrl.trim() : null,
        api_key: formState.value.apiKey.trim() || undefined,
        input_cost_1m: formState.value.inputCost,
        output_cost_1m: formState.value.outputCost,
        is_enabled: formState.value.isEnabled,
      };

      emit('updated', updateData);
    } else {
      const createData: CreateCustomModelRequest = {
        name: formState.value.name.trim(),
        slug: formState.value.slug.trim(),
        sdk_type: formState.value.sdkType,
        base_url: formState.value.sdkType === 'openai_compatible' ? formState.value.baseUrl.trim() : null,
        api_key: formState.value.apiKey.trim(),
        input_cost_1m: formState.value.inputCost,
        output_cost_1m: formState.value.outputCost,
        is_enabled: formState.value.isEnabled,
      };

      emit('created', createData);
    }

    // Reset submitting state after a short delay
    setTimeout(() => {
      isSubmitting.value = false;
    }, 500);
  };

  const handleClose = () => {
    if (isSubmitting.value) return;

    resetForm();
    emit('update:open', false);
    emit('cancel');
  };

  const resetForm = () => {
    formState.value = {
      name: '',
      slug: '',
      sdkType: 'openai_compatible',
      baseUrl: '',
      apiKey: '',
      inputCost: undefined,
      outputCost: undefined,
      isEnabled: true,
    };
    errors.value = {};
    generalError.value = null;
    isSubmitting.value = false;
  };

  const loadEditData = () => {
    if (props.editModel) {
      formState.value = {
        name: props.editModel.name,
        slug: props.editModel.slug,
        sdkType: props.editModel.sdk_type,
        baseUrl: props.editModel.base_url || '',
        apiKey: '', // Never populate API key from server
        inputCost: props.editModel.input_cost_1m ?? undefined,
        outputCost: props.editModel.output_cost_1m ?? undefined,
        isEnabled: props.editModel.is_enabled,
      };
    }
  };

  // Watch for modal open/close
  watch(
    () => props.open,
    newValue => {
      if (newValue) {
        if (isEditMode.value) {
          loadEditData();
        } else {
          resetForm();
        }
      } else {
        resetForm();
      }
    },
    { immediate: true }
  );

  // Watch for editModel changes
  watch(
    () => props.editModel,
    () => {
      if (props.open && isEditMode.value) {
        loadEditData();
      }
    },
    { immediate: true }
  );
</script>
